/**
 * RoleSheetsPage — Bảng phân vai (Role Assignment Sheet) — Vòng Bán kết / Chung kết.
 *
 * Audit 2026-05-21 (F.2): tách khỏi MemorialsPage. Chỉ visible cho đội đã vượt
 * Vòng Sơ loại (`team.competition_status === 'advanced_to_semifinal'`).
 * Admin (ASSIGN_CASES) luôn thấy mọi case + mọi đội để review.
 *
 * Update 2026-05-25 (R2 fix): bổ sung khu vực nộp BẢN STRUCTURED 4 vai cho
 * student. Trước đây student chỉ upload được PDF (legacy) — không có chỗ nộp
 * 4 vai chính thức cho R2/R3 (BE đã có `/role-assignment-sheets/structured`
 * nhưng FE chưa expose). Giờ:
 *   - Round selector: lọc theo `round_type ∈ {semifinal, final}` thuộc case.
 *   - Khi student chọn round → mở [[StructuredRoleSheetDialog]] (chế độ 1 đội).
 *   - Hiển thị trạng thái đã nộp / hạn / penalty cho từng round.
 */
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  Assignment,
  AssignmentInd,
  CloudUpload,
  CloudDownload,
  Delete,
  HourglassEmpty,
  CheckCircle,
  Warning,
} from '@mui/icons-material'

import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery, RoundType } from '../store/api/roundsApi'
import {
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
  useGetTeamRoundSheetQuery,
} from '../store/api/bracketsApi'
import { hasPermission } from '../utils/permissions'
import { PageHeader } from '../components/PageHeader'
import { gradientBrand } from '../theme/brand'
import { getFilterPaperSx } from '../theme/sectionSurfaces'
import { ROLE_LABELS } from '../components/StructuredRoleSheetDialog'

export default function RoleSheetsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: cases = [] } = useGetCasesQuery()
  const { data: allRounds = [] } = useGetRoundsQuery()

  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canSubmitMemorial = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')

  const userTeam = useMemo(
    () => teams.find((t) => t.members.includes(currentUser?.id || '')),
    [teams, currentUser?.id],
  )

  // F.2 — gate cho student: đội đã advance qua Vòng Sơ loại (vào Bán kết R2)
  // hoặc đã vào Chung kết (R3) — đều được vào trang để nộp/xem bảng phân vai.
  const studentTeamAdvanced =
    !!userTeam &&
    ((userTeam as any).competition_status === 'advanced_to_semifinal' ||
      (userTeam as any).competition_status === 'advanced_to_final')

  const accessibleCases = useMemo(() => {
    if (canAssignCases) return cases
    if (userTeam) return cases.filter((c) => (c.assigned_team_ids || []).includes(userTeam.id))
    return []
  }, [cases, canAssignCases, userTeam])

  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  useMemo(() => {
    if (!selectedCaseId && accessibleCases.length > 0) {
      setSelectedCaseId(accessibleCases[0].id)
    }
  }, [selectedCaseId, accessibleCases])

  // Rounds chỉ giữ Bán kết + Chung kết của case đang chọn.
  const hearingRounds = useMemo(
    () =>
      allRounds.filter(
        (r) =>
          r.case_id === selectedCaseId &&
          (r.round_type === RoundType.SEMIFINAL || r.round_type === RoundType.FINAL),
      ),
    [allRounds, selectedCaseId],
  )

  const [selectedRoundId, setSelectedRoundId] = useState<string>('')
  useMemo(() => {
    if (hearingRounds.length === 0) {
      if (selectedRoundId) setSelectedRoundId('')
      return
    }
    if (!hearingRounds.find((r) => r.id === selectedRoundId)) {
      setSelectedRoundId(hearingRounds[0].id)
    }
  }, [hearingRounds, selectedRoundId])

  const selectedRound = hearingRounds.find((r) => r.id === selectedRoundId)
  const roundLabel =
    selectedRound?.round_type === RoundType.FINAL ? 'Chung kết' : 'Bán kết'

  const {
    data: roleSheets = [],
    refetch: refetchRoleSheets,
  } = useListRoleSheetsQuery(selectedCaseId, { skip: !selectedCaseId })
  const [uploadRoleSheet, { isLoading: isUploading }] = useUploadRoleSheetMutation()
  const [deleteRoleSheet] = useDeleteRoleSheetMutation()
  const [downloadRoleSheet] = useLazyDownloadRoleSheetQuery()

  // Round-scoped sheet (structured) của đội hiện tại — chỉ query khi student có
  // team + đã chọn round; admin xem nhanh sheet của đội đầu tiên có thể bị
  // ambigous → admin nên dùng BracketsPage để chọn team cụ thể.
  const teamForQuery = userTeam?.id || ''
  const { data: roundSheetResp, refetch: refetchRoundSheet } = useGetTeamRoundSheetQuery(
    {
      case_id: selectedCaseId,
      team_id: teamForQuery,
      round_id: selectedRoundId,
    },
    { skip: !selectedCaseId || !teamForQuery || !selectedRoundId },
  )

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })


  const canViewAccess = canAssignCases || studentTeamAdvanced

  const handleUpload = async (file?: File) => {
    if (!file || !selectedCaseId) return
    if (file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Bảng phân vai chỉ chấp nhận PDF.', severity: 'error' })
      return
    }
    try {
      await uploadRoleSheet({ caseId: selectedCaseId, file }).unwrap()
      setSnackbar({ open: true, message: `Đã nộp Bảng phân vai: ${file.name}`, severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Nộp thất bại: ${err?.data?.detail || err?.message || 'Lỗi không xác định'}`,
        severity: 'error',
      })
    }
  }

  const handleDownload = async (id: string, filename?: string) => {
    try {
      const blob = await downloadRoleSheet(id).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'bang-phan-vai.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xoá Bảng phân vai này?')) return
    try {
      await deleteRoleSheet(id).unwrap()
      setSnackbar({ open: true, message: 'Đã xoá Bảng phân vai.', severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Xoá thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  const existingStructuredSheet = roundSheetResp?.sheet
  // 2026-05-27: PDF (submission_kind='upload') của team này cho round này cũng
  // được coi là "đã nộp" — trước chỉ check structured → user nộp PDF rồi vẫn
  // thấy "Chưa nộp". Match roleSheets bằng team_id + round_id + submission_kind.
  const existingPdfSheet = roleSheets.find(
    (s) =>
      s.team_id === teamForQuery &&
      s.round_id === selectedRoundId &&
      (s.submission_kind || 'upload') !== 'structured',
  )
  const isStudentSubmitter = !!canSubmitMemorial && !!userTeam && !!studentTeamAdvanced

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Bảng phân vai"
        subtitle="Phân công vai trò Đương sự / Luật sư cho Vòng Bán kết và Chung kết."
        icon={<Assignment />}
      />

      {!canViewAccess && (
        <Alert severity="info" icon={<HourglassEmpty />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Bảng phân vai chưa được mở
          </Typography>
          <Typography variant="body2">
            Tính năng này chỉ áp dụng cho các đội đã vượt qua Vòng Sơ loại
            (vào Vòng Bán kết). Hiện đội{' '}
            {userTeam ? <strong>{userTeam.name}</strong> : 'của bạn'} chưa đủ điều kiện —
            vui lòng theo dõi mục Thông báo để biết kết quả công bố.
          </Typography>
        </Alert>
      )}

      {canViewAccess && (
        <>
          {/* Case + round selector */}
          <Paper elevation={0} sx={(t) => ({ ...getFilterPaperSx(t), mb: 2 })}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              flexWrap="wrap"
            >
              <FormControl fullWidth sx={{ maxWidth: { sm: 360 } }} size="small">
                <InputLabel>Cuộc thi</InputLabel>
                <Select
                  value={selectedCaseId}
                  label="Cuộc thi"
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                >
                  {accessibleCases.length === 0 ? (
                    <MenuItem value="" disabled>
                      — Không có cuộc thi —
                    </MenuItem>
                  ) : (
                    accessibleCases.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.title || '— Chưa đặt tên —'}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ maxWidth: { sm: 260 } }} size="small">
                <InputLabel>Vòng</InputLabel>
                <Select
                  value={selectedRoundId}
                  label="Vòng"
                  onChange={(e) => setSelectedRoundId(e.target.value)}
                  disabled={hearingRounds.length === 0}
                >
                  {hearingRounds.length === 0 ? (
                    <MenuItem value="" disabled>
                      — Chưa mở vòng nào —
                    </MenuItem>
                  ) : (
                    hearingRounds.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}{' '}
                        <Chip
                          size="small"
                          sx={{ ml: 1, height: 18, fontSize: 10 }}
                          label={r.round_type === RoundType.FINAL ? 'Chung kết' : 'Bán kết'}
                          color={r.round_type === RoundType.FINAL ? 'error' : 'info'}
                          variant="outlined"
                        />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* 2026-05-27: thay nút "Nộp bảng phân vai (4 vai)" bằng
                  "Tải phiếu đăng ký" — link Google Doc do BTC quản lý, đội
                  tải về điền + ký offline, scan/upload qua nút "Nộp PDF". */}
              <Button
                component="a"
                href="https://docs.google.com/document/d/1EO1tEyTGaajn2gfL0hcSJ83sPxaxBiJN/edit"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<CloudDownload />}
                sx={{ background: gradientBrand, textTransform: 'none' }}
              >
                Tải phiếu đăng ký
              </Button>

              {isStudentSubmitter && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  disabled={isUploading || !selectedCaseId}
                  sx={{ textTransform: 'none' }}
                >
                  {isUploading ? 'Đang nộp PDF...' : 'Nộp PDF'}
                  <input
                    hidden
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                  />
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Structured (4 vai) status cho vòng đang chọn */}
          {selectedRoundId && teamForQuery && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2 }} variant="outlined">
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <AssignmentInd color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Bảng phân vai 4 vai — Vòng {roundLabel}
                </Typography>
                {existingStructuredSheet ? (
                  existingStructuredSheet.is_late ? (
                    <Chip
                      size="small"
                      color="warning"
                      icon={<Warning />}
                      label={`Muộn ${existingStructuredSheet.minutes_late || 0} phút (−20đ)`}
                    />
                  ) : (
                    <Chip size="small" color="success" icon={<CheckCircle />} label="Đã nộp đúng hạn" />
                  )
                ) : existingPdfSheet ? (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircle />}
                    label="Đã nộp PDF"
                  />
                ) : (
                  <Chip size="small" color="default" variant="outlined" label="Chưa nộp" />
                )}
              </Stack>

              {roundSheetResp?.deadline_iso && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Hạn nộp (T-48h trước phiên):{' '}
                  <b>{new Date(roundSheetResp.deadline_iso).toLocaleString('vi-VN')}</b>
                </Typography>
              )}

              {existingStructuredSheet?.roles && existingStructuredSheet.roles.length > 0 ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Phía đại diện:{' '}
                    <Chip
                      size="small"
                      label={
                        existingStructuredSheet.representing_role === 'plaintiff'
                          ? 'Nguyên đơn'
                          : 'Bị đơn'
                      }
                      color={
                        existingStructuredSheet.representing_role === 'plaintiff'
                          ? 'primary'
                          : 'secondary'
                      }
                    />
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {existingStructuredSheet.roles.map((r) => (
                      <Typography key={r.position} variant="body2" sx={{ ml: 1 }}>
                        • {ROLE_LABELS[r.position]}: <b>{r.full_name}</b>
                        {r.student_id ? ` (MSSV ${r.student_id})` : ''}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : existingPdfSheet ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Đội bạn đã nộp Bảng phân vai cho vòng này.
                </Alert>
              ) : (
                isStudentSubmitter && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Đội bạn chưa nộp Bảng phân vai cho vòng này. Bấm{' '}
                    <b>"Tải phiếu đăng ký"</b> để tải mẫu Google Docs, điền + ký rồi nộp lại
                    qua nút <b>"Nộp PDF"</b>.
                  </Alert>
                )
              )}
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Legacy: danh sách file PDF đã nộp */}
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            File PDF đã nộp
          </Typography>

          {!selectedCaseId ? (
            <Alert severity="info">Chọn cuộc thi để xem Bảng phân vai.</Alert>
          ) : roleSheets.length === 0 ? (
            <Alert severity="info">
              Chưa có Bảng phân vai PDF cho cuộc thi này.
              {isStudentSubmitter && ' Đội bạn có thể nộp bằng nút “Nộp PDF” ở trên.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}><strong>Tên file</strong></TableCell>
                    <TableCell sx={{ minWidth: 160 }}><strong>Đội nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Vai trò</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Kích thước</strong></TableCell>
                    <TableCell sx={{ minWidth: 140 }}><strong>Ngày nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 130 }}><strong>Trạng thái</strong></TableCell>
                    <TableCell align="right" sx={{ minWidth: 130 }}><strong>Thao tác</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roleSheets
                    .filter((s) => s.submission_kind !== 'structured')
                    .map((s) => {
                      const team = teams.find((t) => t.id === s.team_id)
                      const isOwn = userTeam && s.team_id === userTeam.id
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.title || s.file_name}</TableCell>
                          <TableCell>
                            {team?.name || (s.team_id ? '— Đội chưa rõ —' : '— BTC —')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.uploader_role || (s.team_id ? 'team' : 'btc')}
                              color={s.team_id ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {s.file_size != null ? `${(s.file_size / 1024).toFixed(1)} KB` : '—'}
                          </TableCell>
                          <TableCell>
                            {s.uploaded_at ? new Date(s.uploaded_at).toLocaleString('vi-VN') : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={s.team_id ? 'Đã nộp' : 'BTC ban hành'}
                              color={s.team_id ? 'success' : 'info'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => s.id && handleDownload(s.id, s.file_name ?? undefined)}
                              title="Tải xuống"
                            >
                              <CloudDownload fontSize="small" />
                            </IconButton>
                            {isOwn && s.id && (
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(s.id!)}
                                color="error"
                                title="Xoá"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* 2026-05-27: bỏ Structured 4-vai dialog — workflow mới: đội tải
          phiếu đăng ký (Google Docs) → điền + ký offline → nộp qua "Nộp PDF". */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
